import { EventType } from '@atlas/types'
import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import type {
	Article,
	ArticleListParams,
	ArticleStatus,
	ArticleTranslationVariant,
	ArticleType,
	CreateArticleInput,
	UpdateArticleInput,
} from '@/domain/article.types'
import { ArticleTranslationStatus } from '@/domain/article.types'
import { RealtimeConnectionState, subscribeRealtime, useRealtimeConnectionState } from '@/realtime/realtime-dispatcher'
import { articleApi } from '@/services/article.service'

/** React-query hooks for the article pages. */

/** Root of the article-list key family. Exported so other hooks invalidate the same key. */
export const ARTICLES_KEY = 'articles'
/** Root of the single-article key family. */
export const ARTICLE_KEY = 'article'

export const articleKey = (articleId: string) => [ARTICLE_KEY, articleId]
export const articleAvailableActionsKey = (articleId: string) => [...articleKey(articleId), 'available-actions']

export const useArticles = (params: ArticleListParams = {}) =>
	useQuery({ queryKey: [ARTICLES_KEY, params], queryFn: () => articleApi.list(params) })

/**
 * The articles of one brand, of one type. Keyed on the brand code and the type
 * alone, so every surface asking the same question shares one cache entry.
 *
 * Filtered on the **brand axis only**: no market narrowing. The Brand page holds
 * a market code but no applicability ladder for it, so it cannot tell an article
 * classified to the region above the market (which applies) from one on a sibling
 * market (which does not), and narrowing on the code alone would silently drop
 * the former. The campaigns card on that page already follows this rule.
 */
export const useBrandArticles = (brandCode: string | null, articleType: ArticleType) =>
	useQuery({
		queryKey: [ARTICLES_KEY, 'brand', brandCode, articleType],
		queryFn: () =>
			articleApi.list({
				classification: { brand: [brandCode as string] },
				articleType: [articleType],
				sort: 'updatedAt',
				order: 'desc',
			}),
		enabled: brandCode !== null,
	})

export const useArticle = (articleId: string | null) =>
	useQuery({
		queryKey: articleKey(articleId ?? ''),
		queryFn: () => articleApi.get(articleId as string),
		enabled: articleId !== null,
	})

export const useArticleAvailableActions = (articleId: string | null) =>
	useQuery({
		queryKey: articleAvailableActionsKey(articleId ?? ''),
		queryFn: () => articleApi.availableActions(articleId as string),
		enabled: articleId !== null,
	})

/** Facet counts for one axis, honouring the caller's active filter context. */
export const useArticleFacets = (axisCode: string | null, params: ArticleListParams = {}) =>
	useQuery({
		queryKey: [ARTICLES_KEY, 'facets', axisCode, params],
		queryFn: () => articleApi.facets(axisCode as string, params),
		enabled: axisCode !== null,
	})

/**
 * The active context to count ONE axis against: every other axis's selection, but
 * not its own.
 *
 * The endpoint applies whatever `filters[…]` it is given, including the axis being
 * counted. Sent as-is, selecting `brand=numelvi` would make every other brand
 * report nothing and disappear from the rail, so the selection could be narrowed
 * but never widened. Dropping the axis's own values is what keeps its alternatives
 * reachable — the same skip-self rule the library rail follows.
 */
function contextForAxis(axisCode: string, params: ArticleListParams): ArticleListParams {
	const { [axisCode]: _own, ...others } = params.classification ?? {}
	return { ...params, classification: others }
}

/**
 * Facet counts for SEVERAL axes at once — one request per axis, because the
 * endpoint counts a single axis.
 *
 * Only the axes passed are requested, so an axis hidden behind "Add variable"
 * costs nothing until it is revealed. Results stay in place while a selection
 * change refetches, rather than the rail flashing empty.
 */
export const useArticleFacetsForAxes = (axisCodes: readonly string[], params: ArticleListParams = {}) =>
	useQueries({
		queries: axisCodes.map((axisCode) => {
			const context = contextForAxis(axisCode, params)
			return {
				// Same key shape as `useArticleFacets`, so one axis read from either
				// hook shares a single cache entry.
				queryKey: [ARTICLES_KEY, 'facets', axisCode, context],
				queryFn: () => articleApi.facets(axisCode, context),
				placeholderData: keepPreviousData,
			}
		}),
	})

/** Invalidate every article query family — the list, the brand reads and the facets. */
function useInvalidateArticles() {
	const queryClient = useQueryClient()
	return (articleId?: string) => {
		void queryClient.invalidateQueries({ queryKey: [ARTICLES_KEY] })
		if (articleId) void queryClient.invalidateQueries({ queryKey: articleKey(articleId) })
	}
}

export const useCreateArticle = () => {
	const invalidate = useInvalidateArticles()
	return useMutation({
		mutationFn: (body: CreateArticleInput) => articleApi.create(body),
		onSuccess: (article: Article) => invalidate(article.articleId),
	})
}

export const useUpdateArticle = () => {
	const invalidate = useInvalidateArticles()
	return useMutation({
		mutationFn: ({ articleId, patch }: { articleId: string; patch: UpdateArticleInput }) =>
			articleApi.update(articleId, patch),
		onSuccess: (article: Article) => invalidate(article.articleId),
	})
}

/**
 * Move an article's status. A same-state request is a successful no-op server
 * side, so the caller does not have to guard against it; a disallowed edge comes
 * back 422 carrying the statuses that were allowed.
 */
export const useTransitionArticle = () => {
	const invalidate = useInvalidateArticles()
	return useMutation({
		mutationFn: ({ articleId, to }: { articleId: string; to: ArticleStatus }) => articleApi.transition(articleId, to),
		onSuccess: (article: Article) => invalidate(article.articleId),
	})
}

export const useDeleteArticle = () => {
	const invalidate = useInvalidateArticles()
	return useMutation({
		mutationFn: (articleId: string) => articleApi.remove(articleId),
		onSuccess: () => invalidate(),
	})
}

// ─── Translations ────────────────────────────────────────────────────────────

/** Root of the translation-group key family. Exported so the realtime query bridge invalidates the same key. */
export const ARTICLE_TRANSLATIONS_KEY = 'article-translations'

/** Query key for one article's translation group, resolved from any member's id. */
export const articleTranslationsKey = (articleId: string) => [ARTICLE_TRANSLATIONS_KEY, articleId]

/** States a translation job no longer moves out of. A null status means the variant was authored, never translated. */
const TERMINAL_TRANSLATION_STATUSES: ReadonlySet<ArticleTranslationStatus | null | undefined> = new Set([
	ArticleTranslationStatus.READY,
	ArticleTranslationStatus.FAILED,
	null,
	undefined,
])

/** Poll cadence while a translation job is still queued or running, matching the message flow's. */
const REFETCH_INTERVAL_MS = 5000

/**
 * How long a job may sit unchanged before the UI stops waiting on it actively.
 *
 * Fifteen times the message flow's threshold, because the two jobs run on
 * different timescales: the article consumer's Lambda budget is 900 seconds
 * (`ARTICLE_TRANSLATE_WORKER_TIMEOUT_SECONDS`), so a job that has not moved in
 * two minutes is almost certainly just working. Twenty minutes is past that
 * ceiling, so the invocation that owned the row is certainly gone.
 *
 * It is still not proof the job is over: the queue's visibility timeout is six
 * times the Lambda timeout, so a failed invocation can be redelivered up to
 * ninety minutes later and complete. Hence "taking longer than expected" rather
 * than a failure, and hence this bounding only the polling branch — a
 * subscription costs nothing to keep waiting.
 *
 * Exported alongside the two predicates so the strip's and the dialog's
 * taking-longer affordance shares the EXACT formula the polling stop condition
 * uses; two implementations of one invariant drift into a UI that offers a retry
 * while the page keeps polling underneath it.
 */
export const ARTICLE_TRANSLATION_STUCK_AFTER_MS = 20 * 60 * 1000

export const isTranslationNonTerminal = (status: ArticleTranslationStatus | null | undefined) =>
	!TERMINAL_TRANSLATION_STATUSES.has(status)

/** A non-terminal job that has sat unchanged past the threshold — presumed stalled, not presumed failed. */
export const isTranslationStuck = (status: ArticleTranslationStatus | null | undefined, updatedAt: string) =>
	isTranslationNonTerminal(status) && Date.now() - new Date(updatedAt).getTime() > ARTICLE_TRANSLATION_STUCK_AFTER_MS

/** The events that end a translation wait, either way. */
const TRANSLATION_EVENTS = [EventType.ARTICLE_TRANSLATED, EventType.ARTICLE_TRANSLATION_FAILED]

/**
 * An article's translation group — the source plus every locale variant, as a
 * coverage projection without bodies. Waits for each in-flight variant two ways,
 * chosen by connection state (ADR-0020):
 *
 * - **Connected** — a subscription to the translation events of the variants
 *   still in flight. No interval, so a minutes-long job costs nothing while it
 *   runs, which matters more here than for messages.
 * - **Otherwise** — poll every 5s while any variant is non-terminal AND has not
 *   passed the stuck threshold.
 */
export const useArticleTranslations = (articleId: string | null) => {
	const queryClient = useQueryClient()
	const isPushing = useRealtimeConnectionState() === RealtimeConnectionState.CONNECTED

	const query = useQuery<ArticleTranslationVariant[]>({
		queryKey: articleTranslationsKey(articleId ?? ''),
		queryFn: () => articleApi.listTranslations(articleId as string),
		enabled: articleId !== null,
		refetchInterval: (q) => {
			if (isPushing) return false // the subscription does the waiting
			const variants = q.state.data ?? []
			const anyActivelyPending = variants.some(
				(v) => isTranslationNonTerminal(v.translationStatus) && !isTranslationStuck(v.translationStatus, v.updatedAt),
			)
			return anyActivelyPending ? REFETCH_INTERVAL_MS : false
		},
	})

	// A string, not an array: the query returns a fresh array on every refetch, so
	// an array dependency would re-register the subscription each time.
	const pendingIdKey = (query.data ?? [])
		.filter((v) => isTranslationNonTerminal(v.translationStatus))
		.map((v) => v.articleId)
		.join(',')

	useEffect(() => {
		const pendingArticleIds = pendingIdKey ? pendingIdKey.split(',') : []
		if (!isPushing || !articleId || pendingArticleIds.length === 0) return

		// Invalidate the whole group rather than the variant the event names: the
		// event says a job ended, not what it produced, and re-reading is what
		// yields the translated title and summary.
		return subscribeRealtime({ type: TRANSLATION_EVENTS, entityRef: { id: pendingArticleIds } }, () => {
			void queryClient.invalidateQueries({ queryKey: articleTranslationsKey(articleId) })
		})
	}, [isPushing, pendingIdKey, articleId, queryClient])

	return query
}

/**
 * Request an AI translation of a source article into one or more target locales,
 * in a single call.
 *
 * Invalidates rather than patching the group cache from the response, unlike the
 * message flow's equivalent: this endpoint returns full articles while the group
 * cache holds the body-less coverage projection, so writing one into the other
 * would mean maintaining a second shape mapping to close a sub-second window.
 * The request is fast — the worker is the slow part, and that is what
 * `useArticleTranslations` waits on.
 */
export const useRequestArticleTranslation = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ articleId, targetLocales }: { articleId: string; targetLocales: string[] }) =>
			articleApi.requestTranslation(articleId, targetLocales),
		onSuccess: (_targets, { articleId }) => {
			void queryClient.invalidateQueries({ queryKey: articleTranslationsKey(articleId) })
			// The new target rows are articles, so every list that could show them is stale.
			void queryClient.invalidateQueries({ queryKey: [ARTICLES_KEY] })
		},
	})
}
